import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useSWRConfig } from 'swr'
import useSWRMutation from 'swr/mutation'

import { BankAddress } from '@frame/ui-components/address/bank-address'
import { ExitPromptType } from '@frame/ui-components/exit-prompt'
import { Footer } from '@frame/ui-components/footer'
import { LoadingSpinner } from '@frame/ui-components/loading-spinner'
import { WidgetHeaderWithNavigation } from '@frame/ui-components/widget-header'
import { Body } from '@frame/ui-primitives/body'
import { Button } from '@frame/ui-primitives/button'
import { H2 } from '@frame/ui-primitives/h2'
import { H4 } from '@frame/ui-primitives/h4'
import { Input } from '@frame/ui-primitives/input'

import { InternalEventType } from '@frame/lib/api/events/types'
import { FinicityPrepData, TrueworkJsStrategy } from '@frame/lib/api/tasks/types'
import { useRootContext } from '@frame/lib/context'
import { useAutoNavigate } from '@frame/lib/hooks/useAutoNavigate'
import { useDirectTask } from '@frame/lib/hooks/useDirectTask'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { useShowExitPrompt } from '@frame/lib/hooks/useShowExitPrompt'
import { useStore } from '@frame/lib/stores/useStore'
import { cn } from '@frame/lib/utils/cn'
import { formatPhoneNumber } from '@frame/lib/utils/format-phone-number'
import { isSessionInTerminalState } from '@frame/lib/utils/session'
import { validatePhoneNumber } from '@frame/lib/utils/validate-phone-number'
import { routes } from '@frame/routes'
import { DataAttribute } from '@frame/types'

import { BankInstructionsCard } from './bank-instructions-card'
import { Address, BankFormValues } from './types'
import { getTaskId } from './utils'

export function BankContactInfo() {
  const { redirectToSessionStateRoute } = useAutoNavigate()
  const { api, actions } = useRootContext()
  const { handleEvent } = useEventHandler()
  const navigate = useNavigate()
  const { mutate } = useSWRConfig()
  const showExitPrompt = useShowExitPrompt()
  const task = useDirectTask(true)
  const targetPhoneNumber = useStore(({ session }) => session?.target_phone_number)
  const prepData = task?.strategy_info?.prep_data ? (task?.strategy_info?.prep_data as FinicityPrepData) : undefined
  const [isConfigured, setIsConfigured] = useState(!!prepData?.connect_url)
  const taskId = getTaskId(task)

  const { trigger: provideStatedInformation, isMutating } = useSWRMutation(
    ['update', 'employer', taskId],
    (
      _id,
      { arg: { taskId, phoneNumber, address } }: { arg: { taskId: string; phoneNumber: string; address: Address } }
    ) => api.tasks.createBankStatedInfo(phoneNumber, address, taskId),
    {
      onSuccess: async () => {
        handleEvent(InternalEventType.BANK_STATED_INFO_PROVIDED)
        await mutate(['all', 'tasks'])
      },
    }
  )

  useEffect(() => {
    if (task?.strategy_info?.show_employee_company_match_picker && task?.strategy_info?.has_completed_bank_attempt) {
      navigate(`${routes.bankIdentifyIncome}/${taskId}`)
    } else if (isConfigured) {
      // According to the browser (which I do everything it says), we should call navigate
      // in a react hook. So...here it is.
      navigate(`${routes.finicityConnect}/${taskId}`)
    }
  }, [
    isConfigured,
    isMutating,
    taskId,
    navigate,
    task?.strategy_info?.has_completed_bank_attempt,
    task?.strategy_info?.show_employee_company_match_picker,
  ])

  const {
    control,
    handleSubmit,
    clearErrors,
    formState: { errors, isSubmitting, isValid, submitCount },
  } = useForm<BankFormValues>({
    defaultValues: {
      phone_number: targetPhoneNumber ? formatPhoneNumber(targetPhoneNumber) : '',
      address: {
        description: '',
        place_id: '',
      },
    },
  })

  const onSubmit = handleSubmit(async (values: BankFormValues) => {
    const { data: sessionData } = await api.clientSession.getClientSession()

    if (!sessionData) {
      throw Error('Session is missing data. This should not happen.')
    }

    if (isSessionInTerminalState(sessionData.state)) {
      redirectToSessionStateRoute()
      return
    }

    if (taskId && task?.strategy !== TrueworkJsStrategy.BANK) {
      const preparedTask = await actions.addStrategyToDirectTask({
        strategy: TrueworkJsStrategy.BANK,
        directTaskId: taskId,
      })

      if (!preparedTask) {
        throw new Error('Failed to add bank strategy to task')
      }
    }

    const response = await provideStatedInformation({
      taskId,
      phoneNumber: values.phone_number,
      address: values.address,
    })

    if (response?.error) {
      navigate(routes.error)
      return
    }

    setIsConfigured(true)
  })

  const disabled = isSubmitting || (!isValid && submitCount > 0)
  const showAsDisabled = submitCount === 0 && !isValid

  return (
    <>
      <WidgetHeaderWithNavigation
        showExitPrompt={showExitPrompt}
        exitPromptType={ExitPromptType.SESSION}
        title="Getting started"
      />
      <Body data-cy={DataAttribute.BANK_SCREEN_CONTACT_INFO} paddingTopOverride={0} className="h-full p-5">
        <H2 className="mb-4 text-left">Ready to connect your bank accounts?</H2>
        <BankInstructionsCard />
        <H4 className={cn('mb-4 mt-5 font-semibold text-moss-700')}>Provide your contact information</H4>
        <form className="flex flex-col space-y-4 bg-transparent">
          <Controller
            name="phone_number"
            control={control}
            rules={{ required: 'Required', validate: validatePhoneNumber }}
            render={({ field: { value, name, onChange } }) => (
              <Input
                data-cy={DataAttribute.BANK_PHONE_NUMBER_INPUT}
                label="Phone Number"
                type="tel"
                value={value}
                name={name}
                error={errors.phone_number?.message}
                onChange={(e) => onChange(formatPhoneNumber(e.target.value))}
              />
            )}
          />
          <BankAddress
            control={control}
            clearErrors={(key: string) => clearErrors(key as keyof BankFormValues)}
            disabled={isSubmitting}
          />
        </form>
      </Body>
      <Footer
        buttons={{
          primary: (
            <Button
              data-cy={DataAttribute.CONNECT_BANK_BTN}
              disabled={disabled}
              showAsDisabled={showAsDisabled || disabled}
              onClick={onSubmit}
              intent="primary"
              Icon={isSubmitting ? LoadingSpinner : undefined}
              applyCobranding
            >
              Connect now
            </Button>
          ),
        }}
      />
    </>
  )
}
